import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import BRAND from '../config/brand';
import {
  AUDIENCE,
  GOAL_SETTING_MEETING,
  NEW_PLAN_CLIENTS_PER_MONTH,
  PRICES,
  buildBookingPath,
  buildServicesPath,
} from '../config/planningOffers';
import { trackLinkClick, trackMeetingPageView } from '../lib/analytics';
import PublicSiteNav from './PublicSiteNav';
import './PublicLinksPage.css';
import './MeetingPages.css';

const MEETING = GOAL_SETTING_MEETING;

const TOPICS = [
  {
    title: 'Your goals, in your own words',
    body: 'What you want the next few years to look like, and what “enough” means to you for retirement, housing, family and work.',
  },
  {
    title: 'Your general questions',
    body: 'Bring the ones you keep circling back to. I’ll explain how I think about each one and what a real answer would need to know about you.',
  },
  {
    title: 'The frameworks I plan with',
    body: 'One example is the three tax buckets: taxable accounts, tax-deferred accounts like a traditional 401(k), and tax-free accounts like a Roth when the withdrawal rules are met. Once you see how the buckets work together, “Roth or pre-tax?” stops being a guess and becomes a decision you can reason through.',
  },
  {
    title: 'How the comprehensive plan works',
    body: 'What it covers, what I would need from you, and what it costs, so you can decide with the whole picture in front of you.',
  },
];

const TAKEAWAYS = [
  'Which goals matter most to you, and roughly in what order.',
  'How the planning frameworks apply to someone in your general situation.',
  'Which of your questions need personalized analysis before they can be answered well.',
  'Whether a comprehensive plan makes sense for you, or whether you’re fine without one.',
];

const RESERVED_FOR_PLAN = [
  'Personalized tax analysis',
  'Retirement projections built on your numbers',
  'Investment recommendations',
  'A review of your documents, such as tax returns, statements and benefits',
];

function BookButton({ location }) {
  const to = buildBookingPath({ promotionalAngle: location });
  return (
    <Link
      className="public-service-cta public-service-cta--primary"
      to={to}
      onClick={() =>
        trackLinkClick(to, MEETING.cta, {
          destination_type: 'booking',
          button_id: `meeting_page_book_${location}`,
          button_label: MEETING.cta,
          offer_id: MEETING.offerId,
          cta_location: location,
        })
      }
    >
      {MEETING.cta}
    </Link>
  );
}

/**
 * Goal-Setting Meeting details: who it is for, what is discussed, what is
 * reserved for the full plan, and the price, credit, refund and next steps.
 * Every price and policy sentence comes from offerCatalog.json.
 */
export default function GoalSettingMeetingPage() {
  useEffect(() => {
    trackMeetingPageView({ offer_id: MEETING.offerId });
  }, []);

  return (
    <div className="public-linktree-page public-service-page">
      <PublicSiteNav />
      <main className="public-linktree-shell public-service-shell">
        <header className="public-service-header">
          <Link className="public-service-logo" to="/links" aria-label="Back to Bautista Planning links">
            <img src={BRAND.logoSrc} alt="Bautista Planning and Analytics" />
          </Link>
          <p className="public-linktree-kicker">
            {MEETING.name} · {MEETING.durationMinutes} minutes · {MEETING.priceLabel}
          </p>
          <h1>Start with the decisions in front of you.</h1>
          <p>
            Should the next raise go to the 401(k), the mortgage, or a brokerage account? Roth or
            pre-tax this year? Buy now, or keep renting and investing the difference? These
            questions are connected, and it’s hard to answer one without knowing what you’re
            aiming at. The {MEETING.name} is a {MEETING.durationMinutes}-minute conversation where
            we get clear on that.
          </p>
          <div className="public-service-actions public-service-actions--centered">
            <BookButton location="meeting_hero" />
          </div>
        </header>

        <section className="public-linktree-panel public-service-panel" aria-labelledby="meeting-fit-heading">
          <p className="public-linktree-kicker">Who it’s for</p>
          <h2 id="meeting-fit-heading">Households with more moving parts than time</h2>
          <p>
            This meeting fits best if your household earns approximately{' '}
            {AUDIENCE.householdIncomeLabel} or more, or you earn approximately{' '}
            {AUDIENCE.individualIncomeLabel} or more on your own, and your finances now have more
            pieces than you have time to coordinate. Income is one signal, not the whole test. The
            hour is most useful when you have real decisions coming up and you’re willing to act on
            what you decide.
          </p>
          <p>
            If you want a quick answer to one narrow question, the{' '}
            <Link to="/calculators">free calculators</Link> may be a better place to start.
          </p>
        </section>

        <section className="public-linktree-panel public-service-panel" aria-labelledby="meeting-topics-heading">
          <p className="public-linktree-kicker">What we talk about</p>
          <h2 id="meeting-topics-heading">Goals first, then how the pieces fit</h2>
          <ul className="meeting-detail-list">
            {TOPICS.map((topic) => (
              <li key={topic.title}>
                <strong>{topic.title}.</strong> {topic.body}
              </li>
            ))}
          </ul>
        </section>

        <section className="public-linktree-panel public-service-panel" aria-labelledby="meeting-after-heading">
          <p className="public-linktree-kicker">Afterward</p>
          <h2 id="meeting-after-heading">What you should understand when we’re done</h2>
          <p>By the end of the hour, you should be able to say in plain words:</p>
          <ul className="meeting-detail-list">
            {TAKEAWAYS.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p>
            That clarity is yours to keep. If you decide to take it from there on your own, the
            meeting has still done its job.
          </p>
        </section>

        <section className="public-linktree-panel public-service-panel" aria-labelledby="meeting-plan-heading">
          <p className="public-linktree-kicker">What’s saved for the full plan</p>
          <h2 id="meeting-plan-heading">The analysis of your actual numbers</h2>
          <p>
            The {MEETING.name} is a conversation about goals and frameworks. The work on your
            specific numbers happens in the {PRICES.comprehensive} comprehensive financial plan:
          </p>
          <ul className="meeting-detail-list">
            {RESERVED_FOR_PLAN.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <p>
            I keep that line on purpose. A recommendation made without your full picture is a
            guess, so I save recommendations for when I have the full picture.
          </p>
          <p>
            <Link to={buildServicesPath('comprehensive-plan')}>See what the comprehensive plan covers</Link>
          </p>
        </section>

        <section className="public-linktree-panel public-service-panel" aria-labelledby="meeting-price-heading">
          <p className="public-linktree-kicker">Price, credit and refund</p>
          <h2 id="meeting-price-heading">Know the deal before you accept the deal</h2>
          <ul className="meeting-detail-list">
            <li>
              <strong>Price.</strong> {MEETING.priceLabel} for {MEETING.durationMinutes} minutes,
              paid when you book.
            </li>
            <li>
              <strong>Refund.</strong> {MEETING.refundWording}
            </li>
            <li>
              <strong>Credit.</strong> {MEETING.creditWording}
            </li>
            <li>
              <strong>Changing your time.</strong> {MEETING.cancellationWording}
            </li>
            <li>
              <strong>Ongoing planning.</strong> Optional and separate, at {PRICES.ongoingMonthly}{' '}
              per month once a plan is in place.
            </li>
          </ul>
          <p className="public-lead-form-note">
            I take on {NEW_PLAN_CLIENTS_PER_MONTH === 2 ? 'two' : NEW_PLAN_CLIENTS_PER_MONTH} new
            comprehensive planning clients each month. That limit is on full plans, not on{' '}
            {MEETING.name}s.
          </p>
        </section>

        <section className="public-linktree-panel public-service-panel" aria-labelledby="meeting-steps-heading">
          <p className="public-linktree-kicker">Next steps</p>
          <h2 id="meeting-steps-heading">How booking works</h2>
          <ol className="meeting-detail-list meeting-detail-list--steps">
            <li>
              <strong>Pick a time.</strong> It’s held for you while you check out.
            </li>
            <li>
              <strong>Pay {MEETING.priceLabel} by card.</strong> If the payment doesn’t go through,
              you aren’t charged and the time is released.
            </li>
            <li>
              <strong>Sign a short agreement for this one meeting.</strong> The link is emailed to
              you right after payment. It covers the meeting only, not the comprehensive plan or
              ongoing planning.
            </li>
            <li>
              <strong>We meet.</strong> The calendar invite arrives by email.
            </li>
            <li>
              <strong>Then you decide.</strong> If the comprehensive plan makes sense, it has its
              own agreement and the credit above applies. If it doesn’t, you owe nothing more.
            </li>
          </ol>
          <p>
            You’ve likely done plenty right to get here. This hour is about pointing that effort at
            what you actually want.
          </p>
          <div className="public-service-actions public-service-actions--centered">
            <BookButton location="meeting_footer" />
          </div>
        </section>
      </main>
    </div>
  );
}
